/* eslint-disable import/no-cycle */
import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@mantine/core';
import { ShoppingCart } from 'tabler-icons-react';
import { Link } from 'react-router-dom';
import AppFlexbox from '../../../../common/AppFlexbox';
import AppStack from '../../../../common/AppStack';
import AppText from '../../../../common/AppText';
import { Context as StoreUserContext } from '../../../../../providers/StoreUserContextProvider';
import WsCheckoutCartSummary from './WsCheckoutCartSummary';
import WsCheckoutCartProductCard from './WsCheckoutCartProductCard';
import AppCard from '../../../../common/AppCard';
import { triggerNotification } from '../../../../../helpers/notification';
import WsUnavailableProductsAlert from './WsUnavailableProductsAlert';
import {
  useCartController,
  useCartLineItemDetails
} from '../../../../../helpers/checkout/useCartController';
import WsCheckoutCareInstructionsAlert from './WsCheckoutCareInstructionsAlert';

const WsCheckoutCart = ({
  baseStoreUrl,
  blocks,
  config,
  themeConfig,
  ecomStoreTheme,
  colorSchemes,
  mediaQueries,
  sort,
  onNavigate,
  pathname,
  hasFetchedCart,
  editModeEnabled,
  ...rest
}) => {
  const {
    ecomStoreCart,
    isGuest,
    isCartFetching,
    shippingAddress,
    fetchStoreCart,
    checkoutStoreCart,
    mockStoreCartCheckout,
    removeStoreProducts,
    updateStoreCartProducts,
    removeUnavailableStoreCartProducts,
    onShowCartProductCustomization,
    getGuestCartStorage,
    onChangeShippingAddress,
    setFundraiser,
    setCartEmail
  } = useCartController();
  const { lineItems: checkoutLineItemsInfo } = useCartLineItemDetails(
    ecomStoreCart
  );

  const { state } = useContext(StoreUserContext);
  const [checkoutState, setCheckoutState] = useState({
    pkEcomStoreCart: null,
    email: '',
    fundraiser: {
      fkEcomStoreRosterPlayer: null,
      customInput: null
    },
    loading: false,
    loadingCheckout: false,
    loadingMockCheckout: false
  });

  const { isTabletOrSmaller } = mediaQueries;
  const loading = !hasFetchedCart || isCartFetching || !ecomStoreCart;
  const enableCustomFundraiser =
    themeConfig.makeFundraiserInputRequired === 'true';

  const hasFinalSaleProducts = ecomStoreCart?.ecomStoreCartProducts?.some(
    (p) =>
      p.ecomStoreProductVariant.ecomStoreProduct.ecomVendorProduct
        .ecomVendorProductExternalIds.length > 0
  );

  useEffect(() => {
    if (
      ecomStoreCart &&
      checkoutState.pkEcomStoreCart !== ecomStoreCart.pkEcomStoreCart
    ) {
      const guestStorageCart = getGuestCartStorage(ecomStoreCart.fkEcomStore);
      setCheckoutState((c) => ({
        ...c,
        pkEcomStoreCart: ecomStoreCart.pkEcomStoreCart,
        email: guestStorageCart?.email || '',
        fundraiser: guestStorageCart.fundraiser || {
          fkEcomStoreRosterPlayer: null,
          customInput: null
        },
        loading: false,
        loadingCheckout: false,
        loadingMockCheckout: false
      }));
    }
    else {
      setCheckoutState((c) => ({
        ...c,
        loading: false,
        loadingCheckout: false,
        loadingMockCheckout: false
      }));
    }
  }, [ecomStoreCart]);

  const createCheckoutSession = () => {
    setCheckoutState({
      ...checkoutState,
      loadingCheckout: true
    });
    checkoutStoreCart(
      ecomStoreTheme.ecomStore.pkEcomStore,
      {
        email: checkoutState.email,
        ecomCustomerAddress: ecomStoreCart.checkoutCart.freeShipping
          ? null
          : shippingAddress,
        shippingOptions: ecomStoreCart.checkoutCart.shippingCosts.map((s) => ({
          value: s.value,
          label: s.label,
          cost: s.totalInCents,
          fkEcomExternalConnectionType: s.fkEcomExternalConnectionType,
          ecomExternalConnectionExternalId: s.ecomExternalConnectionExternalId
        })),
        fkEcomStoreRosterPlayer:
          checkoutState.fundraiser.fkEcomStoreRosterPlayer,
        fundraiserCustomInput: enableCustomFundraiser
          ? checkoutState.fundraiser.customInput
          : null
      },
      ({ sessionUrl, outOfSync, outOfStock }) => {
        if (outOfSync) {
          triggerNotification(
            outOfStock
              ? 'Some items in your cart are out of stock. Please review your cart before proceeding to checkout.'
              : 'Products have changed since you added them to your cart. Please review your cart before proceeding to checkout.'
          );
          fetchStoreCart(
            ecomStoreTheme.ecomStore.pkEcomStore,
            { includeShipping: true },
            null,
            (error) => triggerNotification(error)
          );
          setCheckoutState({
            ...checkoutState,
            loadingCheckout: false
          });
        }
        else {
          window.location.href = sessionUrl;
        }
      },
      (error) => {
        triggerNotification(error);
        setCheckoutState({
          ...checkoutState,
          loadingCheckout: false
        });
      }
    );
  };

  const createMockCheckoutSession = () => {
    setCheckoutState({
      ...checkoutState,
      loadingMockCheckout: true
    });
    mockStoreCartCheckout(
      ecomStoreTheme.ecomStore.pkEcomStore,
      {
        fkEcomStoreRosterPlayer:
          checkoutState.fundraiser.fkEcomStoreRosterPlayer,
        fundraiserCustomInput: enableCustomFundraiser
          ? checkoutState.fundraiser.customInput
          : null
      },
      ({ secureId }) => {
        onNavigate(
          `${baseStoreUrl}/checkout/cart?secureId=${secureId}&status=success`
        );
      },
      (error) => {
        triggerNotification(error);
        setCheckoutState({
          ...checkoutState,
          loadingMockCheckout: false
        });
      }
    );
  };

  const onRemoveCartProducts = ({
    pkEcomStoreCartProducts,
    pkEcomStoreCartPackages
  }) => {
    setCheckoutState({
      ...checkoutState,
      loading: true
    });
    removeStoreProducts(
      ecomStoreTheme.ecomStore.pkEcomStore,
      {
        pkEcomStoreCartProducts,
        pkEcomStoreCartPackages,
        includeShipping: true
      },
      () => {
        setCheckoutState({
          ...checkoutState,
          loading: false
        });
      },
      (error) => {
        triggerNotification(error);
        setCheckoutState({
          ...checkoutState,
          loading: false
        });
      }
    );
  };

  const onUpdateProducts = ({
    ecomStoreCartProducts,
    ecomStoreCartPackages
  }) => {
    setCheckoutState({
      ...checkoutState,
      loading: true
    });
    updateStoreCartProducts(
      ecomStoreTheme.ecomStore.pkEcomStore,
      {
        ecomStoreCartProducts,
        ecomStoreCartPackages,
        includeShipping: true
      },
      () => {
        setCheckoutState({
          ...checkoutState,
          loading: false
        });
      },
      (error) => {
        triggerNotification(error);
        setCheckoutState({
          ...checkoutState,
          loading: false
        });
      }
    );
  };

  const onRemoveUnavailableProducts = () => {
    setCheckoutState({
      ...checkoutState,
      loading: true
    });
    removeUnavailableStoreCartProducts(
      ecomStoreTheme.ecomStore.pkEcomStore,
      { includeShipping: true },
      () => {
        setCheckoutState({
          ...checkoutState,
          loading: false
        });
        triggerNotification('Cart updated!', 'success');
      },
      (error) => {
        triggerNotification(error);
        setCheckoutState({
          ...checkoutState,
          loading: false
        });
      }
    );
  };

  return (
    <AppStack style={{ flex: 1, gap: 16, margin: '16px 0px' }}>
      <AppFlexbox style={{ padding: isTabletOrSmaller ? '0px 12px' : 0 }}>
        <AppText style={{ fontSize: 32, fontWeight: 700 }}>
          Shopping cart
        </AppText>
      </AppFlexbox>
      {!loading && (
        <WsUnavailableProductsAlert
          baseStoreUrl={baseStoreUrl}
          ecomStoreCart={ecomStoreCart}
          lineItemsInfo={checkoutLineItemsInfo}
          mediaQueries={mediaQueries}
          onNavigate={onNavigate}
          onRemoveUnavailableProducts={onRemoveUnavailableProducts}
          submitting={checkoutState.loading || checkoutState.loadingCheckout}
        />
      )}

      {hasFinalSaleProducts && (
        <WsCheckoutCareInstructionsAlert
          baseStoreUrl={baseStoreUrl}
          ecomStoreCartProducts={ecomStoreCart.ecomStoreCartProducts}
          editModeEnabled={editModeEnabled}
          mediaQueries={mediaQueries}
          onNavigate={onNavigate}
        />
      )}

      <AppFlexbox
        style={{
          alignItems: isTabletOrSmaller ? 'stretch' : 'start',
          gap: isTabletOrSmaller ? 12 : 32,
          flexDirection: isTabletOrSmaller ? 'column' : 'row',
          flexWrap: 'nowrap'
        }}
      >
        {loading && !ecomStoreCart ? (
          <AppStack style={{ flex: 2, gap: 16 }}>
            {Array.from(Array(3)).map((x, i) => (
              <WsCheckoutCartProductCard
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                baseStoreUrl={baseStoreUrl}
                loading
                mediaQueries={mediaQueries}
                onNavigate={onNavigate}
              />
            ))}
          </AppStack>
        ) : ecomStoreCart.checkoutCart.lineItems.length === 0 ? (
          <AppFlexbox style={{ flex: 2 }}>
            <AppCard
              radius={isTabletOrSmaller ? 0 : 'md'}
              shadow="lg"
              style={{ flex: 1, padding: 32 }}
              withBorder
            >
              <AppFlexbox
                style={{
                  flex: 1,
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <ShoppingCart color="#000" size={80} />
                <AppStack style={{ gap: 5 }}>
                  <AppText style={{ fontSize: 20, fontWeight: 500 }}>
                    Your shopping cart is empty.
                  </AppText>
                  <Button
                    color="dark"
                    component={Link}
                    onClick={onNavigate}
                    style={{ marginTop: 5 }}
                    to={`${baseStoreUrl}/products`}
                    variant="outline"
                  >
                    Start shopping
                  </Button>
                </AppStack>
              </AppFlexbox>
            </AppCard>
          </AppFlexbox>
        ) : (
          <AppStack style={{ flex: 2, gap: 16 }}>
            {ecomStoreCart.checkoutCart.lineItems
              .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
              .map((lineItem) => (
                <WsCheckoutCartProductCard
                  key={lineItem.entityId}
                  ecomStoreCart={ecomStoreCart}
                  lineItem={lineItem}
                  lineItemInfo={checkoutLineItemsInfo.find(
                    (p) => p.entityId === lineItem.entityId
                  )}
                  mediaQueries={mediaQueries}
                  onCustomize={(product) => {
                    if (lineItem.isBundleProduct) {
                      onShowCartProductCustomization({
                        ecomStoreCartPackage: ecomStoreCart.ecomStoreCartPackages.find(
                          (p) => p.pkEcomStoreCartPackage === lineItem.entityId
                        )
                      });
                    }
                    else {
                      onShowCartProductCustomization({ecomStoreCartProduct: product});
                    }
                  }}
                  onNavigate={onNavigate}
                  onRemove={onRemoveCartProducts}
                  onRemoveUnavailableProducts={onRemoveUnavailableProducts}
                  onUpdate={onUpdateProducts}
                  productUrl={`${baseStoreUrl}/products/${lineItem?.urlHandle}`}
                  submitting={
                    checkoutState.loading || checkoutState.loadingCheckout
                  }
                />
              ))}
          </AppStack>
        )}

        <AppStack style={{ flex: 1 }}>
          <WsCheckoutCartSummary
            checkoutCart={ecomStoreCart?.checkoutCart}
            checkoutLineItemsInfo={checkoutLineItemsInfo}
            disabled={!!ecomStoreCart?.latestVersionEcomStoreCartProducts}
            ecomStoreRosterPlayers={state.ecomStoreRosterPlayers.value}
            ecomStoreTheme={ecomStoreTheme}
            editModeEnabled={editModeEnabled}
            fundraiser={checkoutState.fundraiser}
            guestEmail={checkoutState.email}
            hasFinalSaleProducts={hasFinalSaleProducts}
            isGuest={isGuest}
            loading={loading || checkoutState.loading}
            loadingCheckout={checkoutState.loadingCheckout}
            loadingMockCheckout={checkoutState.loadingMockCheckout}
            mediaQueries={mediaQueries}
            onChangeShippingAddress={onChangeShippingAddress}
            onCheckout={createCheckoutSession}
            onEmailChange={(email) => {
              setCheckoutState({ ...checkoutState, email });
              setCartEmail(ecomStoreTheme.ecomStore.pkEcomStore, email);
            }}
            onFundraiserChange={(fundraiser) => {
              setCheckoutState({
                ...checkoutState,
                fundraiser: {
                  ...checkoutState.fundraiser,
                  ...fundraiser
                }
              });
              setFundraiser(ecomStoreTheme.ecomStore.pkEcomStore, {
                ...checkoutState.fundraiser,
                ...fundraiser
              });
            }}
            onMockCheckout={createMockCheckoutSession}
            themeConfig={themeConfig}
          />
        </AppStack>
      </AppFlexbox>
    </AppStack>
  );
};

WsCheckoutCart.propTypes = {
  baseStoreUrl: PropTypes.string,
  blocks: PropTypes.array,
  colorSchemes: PropTypes.array,
  config: PropTypes.object,
  ecomStoreTheme: PropTypes.object,
  editModeEnabled: PropTypes.bool,
  hasFetchedCart: PropTypes.bool,
  mediaQueries: PropTypes.object,
  onNavigate: PropTypes.func,
  pathname: PropTypes.string,
  sort: PropTypes.number,
  themeConfig: PropTypes.object
};

export default WsCheckoutCart;
